// src/fieldmapper/seasonTotals.js — ONE SEASON, BY CROP AND TYPE, FROM THE ROWS MACHINE DATA ALREADY HAS
//
// Kevin, 2026-10-31 (the farmer-first Field Mapper home, mockup approved the same day):
// farmers must easily see their fields, fields and acres by crop, yield maps, planting
// maps, and totals for planting and yield by crop and type (grain vs silage), and export
// "a PDF of yield summaries by operation totals (all of a farmers wheat crop, or corn, etc)".
//
// ⚠ NO NEW PIPELINE. Every number here is computed from what the Machine data view already
// loads and by the rules it already applies:
//   - `byField` rows from ?fn=jd-fielddata (api/_jd.js): crop, year (the RMA crop year),
//     plant_ac / plant_date, harv_ac / harv_date, yield + yield_unit (+ yield_dm on silage),
//     planted_as on a CORN planting folded onto its CORN SILAGE chop.
//   - `calibration` from the same payload: one factor per CROP|YEAR, applied to every yield
//     exactly as JdFieldDataView's calY() does. Seed rate is never scaled.
//   - seed_rate grid metadata from ?fn=jd-opsgrids&novalues=1 (the Maps tab's own load):
//     the planting rate and ITS unit, which byField rows do not carry.
//   - policy_fields rows (what Field Mapper already has) for the reported acres per crop.
//
// THE RULES, each one already paid for elsewhere in this repo:
//   - NEVER AVERAGE ACROSS CROPS. A harvest line is keyed by crop AND unit (the 16a78b1
//     cropRows rule): Corn grain and Corn silage are separate lines, and there is no
//     all-crops yield anywhere in this module.
//   - The average divides by the acres it counted production on and by no others
//     (Sullivan's 42.5 bu/ac): acres harvested with no yield are counted as harvested and
//     named, never put in the denominator.
//   - Silage tons in byField are WET tons (the wet_yield branch in api/_jd.js is the only
//     place that sets ton/ac on a byField row), so they read "wet ton". DM rides alongside
//     when the row carries it.
//   - Dry beans are lb/ac (the unit comes from the row; the crop rule is jdYieldUnitLabel's).
//   - A row whose crop is unknown is skipped from crop totals — it cannot be put on a line.
//   - A seeding rate is keyed by crop AND unit too: Austic's soybeans are drilled in lb/ac
//     on some fields and planted in seeds/ac on others; one average of the two is a number
//     that measures nothing.
//
// Plain CommonJS on purpose, like src/fieldProgress/trackedSource.js: App.jsx and the new
// FarmerSeasonHome.jsx import it through CRA's interop, and tools/check-farmer-season-home.js
// require()s it as-is. The crop-name and unit helpers are App.jsx's own (fmCropName,
// jdYieldUnitLabel, jdIsSilage, jdCropUninsured) and are PASSED IN, so there is one
// definition of each and the checker drives the real ones.
"use strict";

const NOT_GROWN = /^(PRF|PASTURE|RANGELAND)/i;
// John Deere's "no crop" and an operation with no crop name. Neither can go on a line.
const isUnknownCrop = (c) => {
  const s = String(c == null ? "" : c).trim();
  // UNSPECIFIED_CROP is John Deere's own "no crop picked". It slipped past this test and
  // printed an "Unknown crop" line on Lawnel's Season page (Kevin, 2026-10-31).
  return !s || s === "?" || /^(UNKNOWN|NONE|NO CROP|UNSPECIFIED|UNSPECIFIED[ _]CROP)$/i.test(s);
};
// Hay and forage that is not on the acreage report. Its machine acres sum every cutting and
// most of it is cut on ground that is not on the policy, so neither its acres nor a season
// average can be stated from what loads here. Kevin, 2026-11-01: data accuracy first.
const UNREPORTED_FORAGE = /^(ALFALFA|CLOVER|GRASS|HAY|MIXED HAY|PASTURE)\b/i;
// Fall-seeded crops belong to the crop year they are harvested in (api/_jd.js rmaCropYear).
// Only needed for the seed-rate GRIDS, which carry the calendar year of the operation;
// byField rows already carry the crop year.
const FALL_SEEDED = /WHEAT|TRITICALE/i;
function gridCropYear(g) {
  const d = String((g && g.date) || "");
  const yr = /^\d{4}/.test(d) ? Number(d.slice(0, 4)) : (g && g.year != null ? Number(g.year) : null);
  if (!Number.isFinite(yr)) return null;
  if (!FALL_SEEDED.test(String((g && g.crop) || ""))) return yr;
  const mo = /^\d{4}-\d{2}/.test(d) ? Number(d.slice(5, 7)) : null;
  return mo != null && mo >= 9 && mo <= 11 ? yr + 1 : yr;
}

const sameYear = (a, b) => String(a) === String(b);
const fieldKey = (r) => String(r.jd_field_id || r.ftf_key || r.field || "");
const num = (v) => (v == null || v === "" ? 0 : Number(v) || 0);
const r1 = (v) => Math.round(v * 10) / 10;

// The calibration factor JdFieldDataView's calFor/calY apply: CROP|YEAR, factor > 0, else 1.
function calFactor(calibration, crop, year) {
  const c = calibration ? calibration[`${String(crop || "").toUpperCase()}|${Number(year)}`] : null;
  return c && c.factor > 0 ? c.factor : 1;
}

// ── How a unit reads ────────────────────────────────────────────────────────
// Per acre: silage is WET tons and says so. Production: the unit without "/ac".
function avgUnitText(u) {
  if (u === "ton/ac") return "wet ton/ac";
  return u || "unit not recorded";
}
function prodUnitText(u) {
  if (u === "bu/ac") return "bu";
  if (u === "ton/ac") return "wet ton";
  if (u === "lb/ac") return "lb";
  if (u && /\/ac$/.test(u)) return u.replace(/\/ac$/, "");
  return null;   // "unit not recorded" — the caller says so rather than guessing
}
const fmtAvg = (v) => (v == null ? "—" : v.toFixed(1));
const fmtAc = (v) => (v == null ? "—" : (Math.round(v * 10) / 10).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const fmtProd = (v) => Math.round(v).toLocaleString("en-US");
// A seeding rate in the thousands is a seed count; below that it is pounds or bushels.
const fmtRate = (v) => (v == null ? "—" : v >= 1000 ? Math.round(v).toLocaleString("en-US") : v.toFixed(1));

// The line label. CORN reads "Corn grain" only when the same view also has silage, which
// is JdFieldDataView's cropLabel exactly — so the two pages name a line the same way.
function lineLabel(crop, hasSilage, h) {
  if (crop === "CORN") return hasSilage ? "Corn grain" : "Corn";
  if (crop === "CORN SILAGE") return "Corn silage";
  return h.cropName(crop);
}
// What is planted is CORN, whatever it was later chopped as.
const plantingCrop = (r) => {
  const c = String(r.planted_as || r.crop || "").toUpperCase().trim();
  return c === "CORN SILAGE" ? "CORN" : c;
};

// ── HARVEST: one line per crop and unit ─────────────────────────────────────
function harvestLines(rows, year, calibration, h) {
  const seen = [], by = {}, noMap = {};
  for (const r of rows) {
    if (!sameYear(r.year, year) || isUnknownCrop(r.crop)) continue;
    const crop = String(r.crop).toUpperCase().trim();
    if (!seen.includes(crop)) seen.push(crop);
    if (!(num(r.harv_ac) > 0)) continue;
    if (r.yield == null) { noMap[crop] = (noMap[crop] || 0) + num(r.harv_ac); continue; }
    const u = h.unitLabel(r.crop, r.yield_unit);
    const k = `${crop}|${u}`;
    const f = calFactor(calibration, r.crop, r.year);
    const a = (by[k] ||= { key: k, crop, unit: u, ac: 0, prod: 0, fields: new Set(), dmAc: 0, dmProd: 0, calibrated: false });
    a.ac += num(r.harv_ac);
    a.prod += num(r.harv_ac) * Number(r.yield) * f;
    a.fields.add(fieldKey(r));
    if (f !== 1) a.calibrated = true;
    // DM scales with the wet figure it was derived from (yield × (1 − moisture)).
    if (r.yield_dm != null) { a.dmAc += num(r.harv_ac); a.dmProd += num(r.harv_ac) * Number(r.yield_dm) * f; }
  }
  const hasSilage = seen.includes("CORN SILAGE");
  const lines = Object.values(by).map((a) => ({
    key: a.key, crop: a.crop, unit: a.unit,
    label: lineLabel(a.crop, hasSilage, h),
    type: a.unit === "ton/ac" || h.isSilage(a.crop) ? "silage" : "grain",
    ac: r1(a.ac), prod: a.prod, avg: a.ac > 0 ? +(a.prod / a.ac).toFixed(1) : null,
    fields: a.fields.size,
    dmAvg: a.dmAc > 0 ? +(a.dmProd / a.dmAc).toFixed(1) : null, dmAc: r1(a.dmAc),
    calibrated: a.calibrated, noMapAc: 0,
  }));
  // A crop harvested two ways (triticale for grain and for silage) would print two lines
  // with one name; the unit already differs, the name says which is which.
  for (const l of lines) {
    if (lines.some((o) => o !== l && o.crop === l.crop) && l.type === "silage" && !/silage/i.test(l.label)) l.label += " silage";
  }
  lines.sort((a, b) => b.ac - a.ac);
  // Harvested acres with no yield on them: named on the crop's biggest line, never averaged.
  for (const c of Object.keys(noMap)) {
    const l = lines.find((x) => x.crop === c);
    if (l) l.noMapAc = r1(noMap[c]);
  }
  const unmapped = Object.keys(noMap).filter((c) => !lines.some((x) => x.crop === c))
    .map((c) => ({ crop: c, label: lineLabel(c, hasSilage, h), noMapAc: r1(noMap[c]) }));
  return { lines, hasSilage, unmapped };
}

// ── PLANTING: acres per planted crop, rate per crop and unit ────────────────
function plantingLines(rows, year, seedGrids, h) {
  const by = {};
  for (const r of rows) {
    if (!sameYear(r.year, year) || !(num(r.plant_ac) > 0)) continue;
    const crop = plantingCrop(r);
    if (isUnknownCrop(crop)) continue;
    const a = (by[crop] ||= { crop, ac: 0, fields: new Set() });
    a.ac += num(r.plant_ac); a.fields.add(fieldKey(r));
  }
  // The Maps tab's Compare average, per crop AND unit: acre-weighted over passes that
  // carry an area, a flat mean only when none does. Coverage passes carry no rate.
  const rate = {};
  for (const g of seedGrids || []) {
    if (!g || g.metric === "coverage" || g.mean == null || !Number.isFinite(Number(g.mean))) continue;
    if (!sameYear(gridCropYear(g), year) || isUnknownCrop(g.crop)) continue;
    const crop = plantingCrop({ crop: g.crop });
    const unit = g.unit || null;
    const k = `${crop}|${unit || ""}`;
    const o = (rate[k] ||= { crop, unit, wsum: 0, w: 0, flat: 0, n: 0 });
    const m = Number(g.mean), ar = g.area != null ? Number(g.area) : 0;
    o.n++; o.flat += m;
    if (ar > 0) { o.wsum += m * ar; o.w += ar; }
  }
  for (const o of Object.values(rate)) if (!by[o.crop]) by[o.crop] = { crop: o.crop, ac: 0, fields: new Set() };
  const lines = Object.values(by).map((a) => {
    const rates = Object.values(rate).filter((o) => o.crop === a.crop).map((o) => ({
      unit: o.unit || "rate unit not recorded",
      avg: o.w > 0 ? +(o.wsum / o.w).toFixed(1) : (o.n ? +(o.flat / o.n).toFixed(1) : null),
      passes: o.n,
    })).sort((x, y) => y.passes - x.passes);
    return { key: a.crop, crop: a.crop, label: a.crop === "CORN" ? "Corn" : h.cropName(a.crop),
             ac: r1(a.ac), fields: a.fields.size, rates, cover: !!(h.isCover && h.isCover(a.crop)) };
  });
  // The crops that are insured first, cover crops after; biggest first within each.
  lines.sort((a, b) => (a.cover ? 1 : 0) - (b.cover ? 1 : 0) || b.ac - a.ac);
  return lines;
}

// ── REPORTED ACRES: policy_fields, per crop and corn type ───────────────────
function fsaLines(policyRows, farmId, h) {
  if (!Array.isArray(policyRows)) return null;
  const keys = new Set(), by = new Map();
  let acres = 0;
  for (const r of policyRows) {
    if (farmId && r.farmer_id !== farmId) continue;
    const crop = String(r.crop || "").toUpperCase().trim();
    if (!crop || NOT_GROWN.test(crop)) continue;
    const clu = String(r.clu_id || "").toLowerCase() || `${r.fsn}-${r.tract}-${r.field_id}`;
    keys.add(clu);
    const ac = r.acres != null ? num(r.acres) : 0;
    acres += ac;
    const tc = crop === "CORN" ? String(r.type_code == null ? "" : r.type_code).trim() : "";
    const k = `${crop}|${tc}`;
    const e = by.get(k) || { key: k, crop, tc, label: h.cropName(crop, tc), acres: 0, clus: new Set() };
    e.acres += ac; e.clus.add(clu); by.set(k, e);
  }
  const lines = [...by.values()].map((e) => ({ key: e.key, crop: e.crop, tc: e.tc, label: e.label, acres: r1(e.acres), fields: e.clus.size }))
    .sort((a, b) => b.acres - a.acres);
  return { fields: keys.size, acres: r1(acres), lines };
}

// Which John Deere crops a reported crop line answers for.
//
// ⚠ CORN IS ONE ROW. Kevin, 2026-11-01: "Lawnel planted 1924 acres of corn total, 1200 or so
// was chopped… that does not leave 1924 left for grain", and then: "I think it just needs to
// show corn and then show how the acres were harvested." Every farm that chops in this book
// insures its corn as 016 grain, so a grain line and a silage line each took their own machine
// corn and the chop printed as a second row ON TOP of all the insured corn. Now every reported
// corn line (016, 026, no type) is ONE Corn row that answers for both John Deere corns; the
// insured types ride along underneath, and the grain and silage yields keep their own names
// and units on it. A chop is never added to grain, and nothing here converts tons to bushels.
function machineCropsFor(line) {
  return line.crop === "CORN" ? ["CORN", "CORN SILAGE"] : [line.crop];
}

// The one Corn row's "how it was harvested", and its appraisal. With the Season corn payload
// (api/_season-corn.js → seasonCorn.js) the acres are each insured field's own machine
// footprint by way of harvest, capped at the field. Before it loads, or where it cannot, the
// machine's own corn and silage acres stand in, capped at the reported acres so the row can
// never read more harvested than there is corn.
// Same words and order as seasonCorn.js's METHOD_TEXT / METHODS (checked by
// tools/check-farmer-season-home.js). "not_yet" means no harvest on file, never "still standing".
const METHOD_TEXT = { combined: "Combined", chopped: "Chopped", appraised: "Appraised, no machine data", unclear: "Harvested, way not clear",
                      map_pending: "Harvested, yield map not built yet", not_yet: "No harvest on file yet", pp: "Prevent plant" };
const METHOD_ORDER = ["combined", "chopped", "appraised", "unclear", "map_pending", "not_yet", "pp"];
function cornDetail(row, corn, fsaAc) {
  const t = corn && corn.totals;
  if (t && corn.fields && corn.fields.length) {
    row.split = METHOD_ORDER.filter((m) => t.acres[m] > 0.05).map((m) => ({ key: m, label: METHOD_TEXT[m], ac: t.acres[m] }));
    row.splitBasis = "fields";
    row.harvAc = r1(Math.min(fsaAc, t.harvestedAc));
    row.stillAc = t.acres.not_yet;
  } else {
    const comb = row._comb || 0;
    const chop = row._chop;
    const both = (comb || 0) + (chop || 0);
    const scale = both > fsaAc && both > 0 ? fsaAc / both : 1;
    const c1 = r1((comb || 0) * scale), c2 = r1((chop || 0) * scale);
    const rest = r1(Math.max(0, fsaAc - c1 - c2));
    row.split = [["combined", c1], ["chopped", c2], ["not_yet", rest]].filter(([, a]) => a > 0.05)
      .map(([m, a]) => ({ key: m, label: METHOD_TEXT[m], ac: a }));
    row.splitBasis = "machine";
    row.harvAc = r1(Math.min(fsaAc, both));
    row.stillAc = rest;
  }
  delete row._comb; delete row._chop;
  // The appraisal, beside the machine yields and never in their place.
  row.appraisal = t && t.appraised ? t.appraised : null;
  // Appraised on acres INSURED AS SILAGE (seasonCorn.js) — shown beside, never in, the grain figures.
  row.appraisalSilageInsured = t && t.appraisedSilageInsured ? t.appraisedSilageInsured : null;
  row.cornFields = corn && corn.fields ? corn.fields : null;
  row.unplacedAppraisals = corn && corn.unplaced ? corn.unplaced : [];
  return row;
}

// ── THE SEASON ──────────────────────────────────────────────────────────────
// h = { cropName(crop, typeCode), unitLabel(crop, unit), isSilage(crop), isCover(crop) }
function buildSeason({ byField = [], calibration = null, policyRows = null, seedGrids = null, year, farmId = null, corn = null } = {}, h) {
  const rows = Array.isArray(byField) ? byField : [];
  const harvest = harvestLines(rows, year, calibration, h);
  const planting = plantingLines(rows, year, seedGrids, h);
  const fsa = fsaLines(policyRows, farmId, h);

  const acBy = (crops, k) => rows.reduce((s, r) => (sameYear(r.year, year) && !isUnknownCrop(r.crop)
    && crops.includes(String(r.crop).toUpperCase().trim()) && num(r[k]) > 0 ? s + num(r[k]) : s), 0);
  const fieldsBy = (crops) => new Set(rows.filter((r) => sameYear(r.year, year) && !isUnknownCrop(r.crop)
    && crops.includes(String(r.crop).toUpperCase().trim()) && (num(r.plant_ac) > 0 || num(r.harv_ac) > 0 || r.harv_date || r.plant_date))
    .map(fieldKey)).size;

  // The home's By crop rows: every reported crop line, then any crop the machines
  // harvested that is not on the acreage report (alfalfa, triticale silage), so a
  // farmer's yield is never missing from the page because of where its acres live.
  const claimed = new Set();
  const cropRows = [];
  const fsaRows = fsa ? fsa.lines : [];
  const cornLines = fsaRows.filter((l) => l.crop === "CORN");
  for (const l of fsaRows) {
    if (l.crop === "CORN" && l !== cornLines[0]) continue;     // folded into the one Corn row
    const mc = machineCropsFor(l);
    mc.forEach((c) => claimed.add(c));
    const isCornRow = l.crop === "CORN";
    const fsaAc = isCornRow ? r1(cornLines.reduce((s, x) => s + x.acres, 0)) : l.acres;
    const fsaFields = isCornRow ? cornLines.reduce((s, x) => s + x.fields, 0) : l.fields;
    const row = { key: isCornRow ? "fsa:CORN" : `fsa:${l.key}`, label: isCornRow ? "Corn" : l.label, crop: l.crop,
                  tc: isCornRow ? (cornLines.length === 1 ? l.tc : "") : l.tc, machineCrops: mc,
                  fsaAc, fsaFields,
                  yields: harvest.lines.filter((x) => mc.includes(x.crop)),
                  harvAc: r1(acBy(mc, "harv_ac")), plantAc: r1(acBy(mc, "plant_ac")), machineFields: fieldsBy(mc) };
    if (isCornRow) {
      row._comb = acBy(["CORN"], "harv_ac"); row._chop = acBy(["CORN SILAGE"], "harv_ac");
      // The insured types under the one row, when there is more than one (Anchor insures both).
      row.types = cornLines.length > 1 ? cornLines.map((x) => ({ tc: x.tc, label: x.label, acres: x.acres, fields: x.fields })) : null;
      cornDetail(row, corn, fsaAc);
    }
    cropRows.push(row);
  }
  const extra = new Map();
  const dropped = [];
  for (const x of harvest.lines) {
    if (claimed.has(x.crop)) continue;
    if (UNREPORTED_FORAGE.test(x.crop)) { if (!dropped.includes(x.crop)) dropped.push(x.crop); continue; }
    const e = extra.get(x.crop) || { key: `jd:${x.crop}`, label: x.label.replace(/ silage$/, ""), crop: x.crop, tc: "", machineCrops: [x.crop],
                                     fsaAc: null, fsaFields: null, yields: [] };
    e.yields.push(x); extra.set(x.crop, e);
  }
  for (const e of extra.values()) {
    e.label = e.yields.length === 1 ? e.yields[0].label : e.label;
    e.harvAc = r1(acBy(e.machineCrops, "harv_ac")); e.plantAc = r1(acBy(e.machineCrops, "plant_ac"));
    e.machineFields = fieldsBy(e.machineCrops);
    cropRows.push(e);
  }

  // HARVEST PROGRESS, over the reported crops the machines have touched this season. A
  // crop with no machine record at all (hay cut with a mower, a field run by a neighbour
  // with no data) would hold the bar down forever and say nothing true about the harvest.
  // Without reported acres, the planted acres are the denominator instead.
  let progress = null;
  const tracked = cropRows.filter((r) => r.fsaAc != null && (r.harvAc > 0 || r.plantAc > 0));
  if (tracked.length) {
    const of = tracked.reduce((s, r) => s + r.fsaAc, 0), ac = tracked.reduce((s, r) => s + r.harvAc, 0);
    progress = { ac: r1(ac), of: r1(of), pct: of > 0 ? Math.min(100, Math.round((100 * ac) / of)) : null, basis: "reported" };
  } else if (!fsa || !fsa.lines.length) {
    const of = planting.reduce((s, l) => s + l.ac, 0);
    const ac = rows.reduce((s, r) => (sameYear(r.year, year) && !isUnknownCrop(r.crop) && num(r.harv_ac) > 0 ? s + num(r.harv_ac) : s), 0);
    if (of > 0 || ac > 0) progress = { ac: r1(ac), of: r1(of), pct: of > 0 ? Math.min(100, Math.round((100 * ac) / of)) : null, basis: "planted" };
  }
  const stillToHarvest = cropRows
    .map((r) => ({ r, left: r.stillAc != null ? r.stillAc : r.fsaAc - r.harvAc }))
    .filter(({ r, left }) => r.fsaAc != null && (r.harvAc > 0 || r.plantAc > 0) && left >= 0.5)
    .map(({ r, left }) => ({ label: r.label, ac: r1(left) }));

  const yr = rows.filter((r) => sameYear(r.year, year) && !isUnknownCrop(r.crop));
  // Machine work John Deere has no crop on (UNSPECIFIED_CROP). It cannot go on a crop line, so
  // it is said once, with what fixes it, rather than printed as a crop called "Unknown".
  const noCrop = rows.filter((r) => sameYear(r.year, year) && isUnknownCrop(r.crop));
  const unknownCrop = noCrop.length ? { harvAc: r1(noCrop.reduce((s, r) => s + num(r.harv_ac), 0)),
                                        plantAc: r1(noCrop.reduce((s, r) => s + num(r.plant_ac), 0)) } : null;
  const mapCounts = {
    yieldFields: new Set(yr.filter((r) => num(r.harv_ac) > 0 && r.yield != null).map(fieldKey)).size,
    plantFields: new Set(yr.filter((r) => num(r.plant_ac) > 0).map(fieldKey)).size,
  };
  const machineFields = new Set(yr.filter((r) => num(r.plant_ac) > 0 || num(r.harv_ac) > 0).map(fieldKey)).size;

  // The PDF and the harvest list read the same lines the By crop table does.
  const shownHarvest = harvest.lines.filter((x) => !dropped.includes(x.crop));
  const shownUnmapped = harvest.unmapped.filter((u) => claimed.has(u.crop) || !UNREPORTED_FORAGE.test(u.crop));
  return { year, fsa, harvest: shownHarvest, hasSilage: harvest.hasSilage, harvestUnmapped: shownUnmapped,
           forageNotShown: dropped, unknownCrop, corn: corn || null,
           planting, cropRows, progress, stillToHarvest, mapCounts, machineFields };
}

// ── ONE CROP'S FIELDS, for the crop page and the PDF ────────────────────────
// The same per-row states By field prints: a yield, "no yield map" (harvested, no
// yield), "no acres recorded" (a harvest date and nothing measured), "not harvested".
function cropFields(byField, year, machineCrops, calibration, h) {
  const out = [];
  for (const r of byField || []) {
    if (!sameYear(r.year, year) || isUnknownCrop(r.crop)) continue;
    const crop = String(r.crop).toUpperCase().trim();
    if (!machineCrops.includes(crop)) continue;
    const harv = num(r.harv_ac) > 0;
    const f = calFactor(calibration, r.crop, r.year);
    const y = harv && r.yield != null ? Math.round(Number(r.yield) * f * 10) / 10 : null;
    const unit = y != null ? h.unitLabel(r.crop, r.yield_unit) : null;
    out.push({
      key: `${fieldKey(r)}|${crop}`, jd_field_id: r.jd_field_id || null, field: r.field || "Field", crop,
      ac: harv ? num(r.harv_ac) : num(r.plant_ac) > 0 ? num(r.plant_ac) : (r.field_acres != null ? num(r.field_acres) : null),
      plant_ac: num(r.plant_ac) > 0 ? num(r.plant_ac) : null, harv_ac: harv ? num(r.harv_ac) : null,
      plant_date: r.plant_date || null, harv_date: r.harv_date || null,
      yield: y, unit, prod: y != null ? num(r.harv_ac) * y : null,
      dm: y != null && r.yield_dm != null ? Math.round(Number(r.yield_dm) * f * 10) / 10 : null,
      state: y != null ? "yield" : harv ? "no_map" : r.harv_date ? "no_acres" : "not_harvested",
    });
  }
  return out;
}

// Each harvest line's own fields: a field with a yield goes under the line of its crop AND
// unit; a field harvested with no yield goes under the line that names those acres.
function fieldsByLine(byField, year, lines, calibration, h) {
  const out = {};
  for (const l of lines || []) {
    out[l.key] = cropFields(byField, year, [l.crop], calibration, h)
      .filter((r) => (r.yield != null && r.unit === l.unit) || (r.state === "no_map" && l.noMapAc > 0));
  }
  return out;
}

const STATE_TEXT = { no_map: "no yield map", no_acres: "no acres recorded", not_harvested: "not harvested" };
const mdText = (d) => {
  if (!d || !/^\d{4}-\d{2}-\d{2}/.test(String(d))) return null;
  const M = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${M[Number(String(d).slice(5, 7)) - 1]} ${Number(String(d).slice(8, 10))}`;
};

module.exports = {
  buildSeason, cropFields, fieldsByLine, harvestLines, plantingLines, fsaLines, machineCropsFor, cornDetail, METHOD_TEXT,
  avgUnitText, prodUnitText, fmtAvg, fmtAc, fmtProd, fmtRate, mdText, STATE_TEXT,
  calFactor, gridCropYear, isUnknownCrop, lineLabel,
};
